import { useState, useMemo } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import FichaContrato from "./FichaContrato";
import { alertasContrato, arreglosAbiertos } from "../data/contratoDetalle";
import { PageHeader } from "../components/AppLayout";
import { StatusBadge } from "../components/StatusBadge";
import { Search, Download, Send, FileText, TrendingUp, Wrench } from "lucide-react";
import { toast } from "sonner";
import {
  CONTRATOS,
  COBROS,
  RECLAMOS,
  liquidacionesDePropietario,
  propiedadPorId,
} from "../data/mockData";
import { pesos, fechaCorta } from "../lib/format";
import { generarLiquidacionPDF } from "../lib/liquidacionPdf";
import { useBrand } from "../context/BrandContext";

export default function Alquileres() {
  const { brand } = useBrand();
  const [tab, setTab] = useState("contratos");

  return (
    <div>
      <PageHeader
        titulo="Administración de alquileres"
        descripcion="Contratos, cobros del mes, ajustes automáticos, reclamos de mantenimiento y liquidación al propietario. Funciona igual de claro con 5 contratos o con 500."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-md mb-5 flex-wrap h-auto">
          <TabsTrigger value="contratos" data-testid="rentals-tab-contratos" className="rounded-md">Contratos</TabsTrigger>
          <TabsTrigger value="cobros" data-testid="rentals-tab-cobros" className="rounded-md">Cobros del mes</TabsTrigger>
          <TabsTrigger value="ajustes" data-testid="rentals-tab-ajustes" className="rounded-md">Ajustes</TabsTrigger>
          <TabsTrigger value="reclamos" data-testid="rentals-tab-reclamos" className="rounded-md">Reclamos</TabsTrigger>
          <TabsTrigger value="liquidaciones" data-testid="rentals-tab-liquidaciones" className="rounded-md">Liquidaciones</TabsTrigger>
        </TabsList>

        <TabsContent value="contratos"><Contratos brand={brand} /></TabsContent>
        <TabsContent value="cobros"><Cobros /></TabsContent>
        <TabsContent value="ajustes"><Ajustes /></TabsContent>
        <TabsContent value="reclamos"><Reclamos /></TabsContent>
        <TabsContent value="liquidaciones"><Liquidaciones brand={brand} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Contratos({ brand }) {
  const [q, setQ] = useState("");
  const [indice, setIndice] = useState("todos");
  const [estado, setEstado] = useState("todos");
  const [sel, setSel] = useState(null);
  const [estadosOverride, setEstadosOverride] = useState({});
  const estadoDe = (c) => estadosOverride[c.id] ?? c.estado;

  const filtrados = useMemo(() => {
    const t = q.toLowerCase();
    return CONTRATOS.filter(
      (c) =>
        (c.inquilino.nombre.toLowerCase().includes(t) ||
          c.propietario.nombre.toLowerCase().includes(t) ||
          c.direccion.toLowerCase().includes(t) ||
          c.barrio.toLowerCase().includes(t)) &&
        (indice === "todos" || c.indice === indice) &&
        (estado === "todos" || c.estado === estado)
    );
  }, [q, indice, estado]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por inquilino, propietario o dirección…"
            className="pl-9 rounded-md"
            data-testid="rentals-search-input"
          />
        </div>
        <Select value={indice} onValueChange={setIndice}>
          <SelectTrigger data-testid="rentals-indexation-filter" className="w-full sm:w-[160px] rounded-md"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los índices</SelectItem>
            <SelectItem value="ICL">ICL</SelectItem>
            <SelectItem value="IPC">IPC</SelectItem>
            <SelectItem value="Fijo">Fijo</SelectItem>
          </SelectContent>
        </Select>
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger data-testid="rentals-state-filter" className="w-full sm:w-[160px] rounded-md"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            <SelectItem value="Vigente">Vigente</SelectItem>
            <SelectItem value="Por vencer">Por vencer</SelectItem>
            <SelectItem value="Vencido">Vencido</SelectItem>
            <SelectItem value="En salida">En salida</SelectItem>
            <SelectItem value="Finalizado">Finalizado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="rentals-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Dirección</TableHead>
                <TableHead>Inquilino</TableHead>
                <TableHead>Propietario</TableHead>
                <TableHead>Día de pago</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead>Índice</TableHead>
                <TableHead>Próx. ajuste</TableHead>
                <TableHead>Vencimiento</TableHead>
                <TableHead>Arreglos</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Alertas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtrados.map((c) => (
                <TableRow
                  key={c.id}
                  onClick={() => setSel(c)}
                  data-testid={`contract-row-${c.id}`}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <TableCell className="font-medium text-slate-900">
                    {c.direccion}
                    <span className="block text-xs text-slate-400">{c.barrio}</span>
                    {c.situacion && (
                      <span className="mt-1 inline-block rounded-md bg-brand-light px-1.5 py-0.5 text-[10px] font-medium text-brand">
                        {c.situacion}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm">{c.inquilino.nombre}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{c.propietario.nombre}</TableCell>
                  <TableCell className="text-slate-600 text-sm whitespace-nowrap">{c.diaPago}</TableCell>
                  <TableCell className="text-right font-medium">{pesos(c.montoMensual)}</TableCell>
                  <TableCell><StatusBadge estado={c.indice} /></TableCell>
                  <TableCell className="text-slate-600 text-sm">{fechaCorta(c.proximaActualizacion)}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{fechaCorta(c.vencimiento)}</TableCell>
                  <TableCell className="text-center text-sm">
                    {arreglosAbiertos(c) > 0 ? (
                      <span className="font-semibold text-amber-700" data-testid={`contract-open-repairs-${c.id}`}>
                        {arreglosAbiertos(c)}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </TableCell>
                  <TableCell><StatusBadge estado={estadoDe(c)} /></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1" data-testid={`contract-alerts-${c.id}`}>
                      {alertasContrato(c).map((a) => {
                        const tone = {
                          red: "bg-red-50 text-red-800 border-red-200",
                          yellow: "bg-yellow-50 text-yellow-800 border-yellow-200",
                          blue: "bg-blue-50 text-blue-800 border-blue-200",
                          purple: "bg-purple-50 text-purple-800 border-purple-200",
                        }[a.tone];
                        return (
                          <span key={a.texto} className={`rounded-md border px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap ${tone}`}>
                            {a.texto}
                          </span>
                        );
                      })}
                      {alertasContrato(c).length === 0 && <span className="text-slate-300">—</span>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
          {filtrados.length} de {CONTRATOS.length} contratos
        </div>
      </Card>

      <FichaContrato key={sel?.id || "none"} contrato={sel} onClose={() => setSel(null)} onEstadoChange={(id, nuevo) => setEstadosOverride((p) => ({ ...p, [id]: nuevo }))} />
    </div>
  );
}

function Cobros() {
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("todos");
  const filtrados = COBROS.filter(
    (c) =>
      (c.inquilino.toLowerCase().includes(q.toLowerCase()) || c.direccion.toLowerCase().includes(q.toLowerCase())) &&
      (estado === "todos" || c.estado === estado)
  );
  const total = (e) => COBROS.filter((c) => c.estado === e).reduce((s, c) => s + c.monto, 0);

  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        {[
          { e: "Pagado", c: "emerald" },
          { e: "Pendiente", c: "yellow" },
          { e: "Atrasado", c: "red" },
        ].map((x) => (
          <Card key={x.e} className="p-4 rounded-lg border-slate-200 shadow-sm">
            <StatusBadge estado={x.e} />
            <p className="mt-2 text-lg font-bold text-slate-900">{pesos(total(x.e))}</p>
            <p className="text-xs text-slate-400">{COBROS.filter((c) => c.estado === x.e).length} cobros</p>
          </Card>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cobro…" className="pl-9 rounded-md" data-testid="cobros-search-input" />
        </div>
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger className="w-full sm:w-[160px] rounded-md"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="Pagado">Pagado</SelectItem>
            <SelectItem value="Pendiente">Pendiente</SelectItem>
            <SelectItem value="Atrasado">Atrasado</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Card className="rounded-lg border-slate-200 shadow-sm overflow-x-auto">
        <Table data-testid="cobros-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Inquilino</TableHead>
              <TableHead>Dirección</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead>Vencimiento</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acción</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtrados.map((c) => (
              <TableRow key={c.id} className="hover:bg-slate-50">
                <TableCell className="font-medium text-slate-900">{c.inquilino}</TableCell>
                <TableCell className="text-slate-600">{c.direccion}</TableCell>
                <TableCell className="text-right font-medium">{pesos(c.monto)}</TableCell>
                <TableCell className="text-slate-600 text-sm">{fechaCorta(c.vencimiento)}</TableCell>
                <TableCell><StatusBadge estado={c.estado} /></TableCell>
                <TableCell className="text-right">
                  {c.estado !== "Pagado" && (
                    <Button
                      size="sm" variant="outline" className="rounded-md"
                      data-testid={`cobro-remind-${c.id}`}
                      onClick={() => toast.success(`Recordatorio de pago enviado por WhatsApp a ${c.inquilino}.`)}
                    >
                      <Send className="mr-1.5 h-3.5 w-3.5" /> Recordar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function Ajustes() {
  const proximos = [...CONTRATOS]
    .filter((c) => {
      const d = (new Date(c.proximaActualizacion) - new Date()) / 86400000;
      return d >= -5 && d <= 90;
    })
    .sort((a, b) => new Date(a.proximaActualizacion) - new Date(b.proximaActualizacion));

  return (
    <div>
      <Card className="p-4 rounded-lg border-slate-200 shadow-sm mb-4 flex items-start gap-3">
        <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5" />
        <p className="text-sm text-slate-600">
          El sistema avisa el <span className="font-medium text-slate-800">nuevo monto</span> antes de cada actualización, según el índice del contrato (ICL / IPC / fijo). Enviá el aviso al inquilino con un clic.
        </p>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {proximos.map((c) => (
          <Card key={c.id} className="p-4 rounded-lg border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-900">{c.direccion}</p>
              <StatusBadge estado={c.indice} />
            </div>
            <p className="text-xs text-slate-500 mb-3">{c.inquilino.nombre} · ajusta el {fechaCorta(c.proximaActualizacion)}</p>
            <div className="flex items-center gap-3 mb-3">
              <div>
                <p className="text-[11px] text-slate-400">Monto actual</p>
                <p className="text-sm text-slate-500 line-through">{pesos(c.montoMensual)}</p>
              </div>
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              <div>
                <p className="text-[11px] text-slate-400">Nuevo monto ({c.ajustePct}%)</p>
                <p className="text-base font-bold text-emerald-700">{pesos(c.proximoMontoEstimado)}</p>
              </div>
            </div>
            <Button size="sm" className="btn-brand rounded-md w-full" data-testid={`ajuste-notify-${c.id}`}
              onClick={() => toast.success(`Aviso de nuevo monto enviado a ${c.inquilino.nombre}.`)}>
              <Send className="mr-1.5 h-4 w-4" /> Avisar nuevo monto
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Reclamos() {
  const [reclamos, setReclamos] = useState(RECLAMOS);
  const avanzar = (id) => {
    const orden = ["Abierto", "Asignado", "En curso", "Cerrado"];
    setReclamos((rs) =>
      rs.map((r) => {
        if (r.id !== id) return r;
        const i = orden.indexOf(r.estado);
        const nuevo = orden[Math.min(i + 1, 3)];
        return { ...r, estado: nuevo, asignadoA: r.asignadoA || "Service asignado" };
      })
    );
    toast.success("Reclamo actualizado. Se notifica al inquilino.");
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {reclamos.map((r) => (
        <Card key={r.id} data-testid={`reclamo-card-${r.id}`} className="p-4 rounded-lg border-slate-200 shadow-sm">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-900">{r.titulo}</span>
            </div>
            <StatusBadge estado={r.estado} />
          </div>
          <p className="text-xs text-slate-500 mb-1">{r.direccion} · {r.inquilino}</p>
          <p className="text-xs text-slate-400 mb-3">{r.categoria} · Prioridad {r.prioridad} · Reportado {fechaCorta(r.fechaReporte)}</p>
          {r.asignadoA && <p className="text-xs text-slate-600 mb-3">Asignado a: <span className="font-medium">{r.asignadoA}</span></p>}
          {r.estado !== "Cerrado" && (
            <Button size="sm" variant="outline" className="rounded-md w-full" data-testid={`reclamo-advance-${r.id}`} onClick={() => avanzar(r.id)}>
              Avanzar estado
            </Button>
          )}
        </Card>
      ))}
    </div>
  );
}

function Liquidaciones({ brand }) {
  // Agrupamos por propietario únicos
  const propietarios = [...new Set(CONTRATOS.map((c) => c.propietario.nombre))].slice(0, 12);
  const [sel, setSel] = useState(propietarios[0]);
  const liqs = liquidacionesDePropietario(sel);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-center">
        <p className="text-sm text-slate-600">Propietario:</p>
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger className="w-full sm:w-[280px] rounded-md" data-testid="liquidacion-owner-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {propietarios.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {liqs.map((l) => (
          <Card key={l.id} className="rounded-lg border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-900">{l.direccion}</span>
            </div>
            <div className="px-4 py-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Alquiler cobrado</span><span className="font-medium">{pesos(l.montoAlquiler)}</span></div>
              {l.gastos.map((g, i) => (
                <div key={i} className="flex justify-between"><span className="text-slate-500">{g.concepto}</span><span className="text-red-700">{pesos(g.monto)}</span></div>
              ))}
              <div className="flex justify-between pt-2 border-t border-slate-100 mt-2"><span className="font-semibold text-slate-900">Neto al propietario</span><span className="font-bold text-emerald-700">{pesos(l.neto)}</span></div>
            </div>
            <div className="px-4 py-3 border-t border-slate-100">
              <Button
                size="sm" className="btn-brand rounded-md w-full"
                data-testid={`rentals-download-pdf-button-${l.contratoId}`}
                onClick={() => {
                  generarLiquidacionPDF({ agencia: brand.nombre, color: brand.color, liquidacion: l, periodo: "Mes en curso" });
                  toast.success("Liquidación descargada en PDF.");
                }}
              >
                <Download className="mr-1.5 h-4 w-4" /> Descargar liquidación (PDF)
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
